import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Input,
  Button,
} from "@material-tailwind/react";
import faqData from "../data/faqData";
import { useTranslation } from 'react-i18next';
import { useTheme } from '../DarkThemeContext';
import { PageViewTracker } from "../handlers/GoogleAnalyticsHandler";
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

function Faq() {
  // Track page view for Google Analytics
  PageViewTracker({ path: "/faq", title: "FAQ" });

  // Get translation and theme information
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { theme } = useTheme();

  // State variables
  const [openTab, setOpenTab] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTopic, setSelectedTopic] = useState(faqData[0]?.topic || null); // Set first topic as default
  const [faqIndex, setFaqIndex] = useState(null);

  // Get parameters from the URL using React Router's useParams
  const { sectionValue, faqValue } = useParams();

  // Handler for opening/closing accordion items
  const handleAccordionOpen = (index, faq) => {
    // Determine whether the tab is open or closed based on the index
    const isTabClosed = openTab === index;

    // Toggle the state of the tab
    setOpenTab(isTabClosed ? null : index);

    // Construct the URL based on the open accordion question and the selected topic
    let url = '/faq';
    if (selectedTopic) {
      url += `/${selectedTopic}`;
      if (faq && faq.value && !isTabClosed) {
        url += `/${faq.value}`;
      }
    }

    // Update the browser URL without triggering a page reload
    window.history.replaceState(null, "", url);
  };

  // Handler for updating the search term
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setOpenTab(null); // Reset open tabs when searching
  };

  // Handler for selecting a topic
  const handleTopicSelect = (value) => {
    // Reset openTab first to ensure accordion closes
    setOpenTab(null);
    
    // Then update the other states
    setSelectedTopic(value);
    setSearchTerm(""); 
    setFaqIndex(null); // Assurez-vous que faqIndex est également réinitialisé
  
    // Update URL
    let url = '/faq';
    url += `/${value}`;
    window.history.replaceState(null, "", url);
  };

  // Filter FAQs based on the search term and current language
  const filteredFaqs = (faqs, currentLang) => {
    return faqs.filter(faq =>
      currentLang
      && faq.question[currentLang]
      && faq.question[currentLang].toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get recommended topics based on the search term and current language
  const recommendedTopics = faqData.flatMap(({ faqs }) => filteredFaqs(faqs, currentLang));

  // Check if the URL contains either the topic or the topic + question value
  const onLoad = () => {
    if (sectionValue) {
      // If sectionValue is present in the URL, select the corresponding topic
      setSelectedTopic(sectionValue);
  
      // Important: si faqValue n'est pas présent, réinitialiser explicitement openTab
      if (!faqValue) {
        setOpenTab(null);
        setFaqIndex(null);
      }
      // Si faqValue est présent, ouvrir l'accordéon correspondant
      else if (faqValue) {
        const topicIndex = faqData.findIndex((topicData) => topicData.topic === sectionValue);
        if (topicIndex !== -1) {
          const foundFaqIndex = faqData[topicIndex]?.faqs.findIndex((faq) => faq.value === faqValue);
          if (foundFaqIndex !== -1) {
            setFaqIndex(foundFaqIndex);
            setOpenTab(foundFaqIndex);
          }
        }
      }
    }
  }

  useEffect(() => {
    const element = document.getElementById(`faq${faqIndex}`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [faqIndex]);

  // useLayoutEffect for immediate execution with a delay
  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(() => {
      onLoad();
    });

    return () => cancelAnimationFrame(rafId);
  }, [sectionValue, faqValue]);

  // Get the faqs to display based on selected topic or search
  const faqsToDisplay = searchTerm 
    ? recommendedTopics 
    : faqData.find(({ topic }) => topic === selectedTopic)?.faqs || [];

  return (
    <div className="flex flex-col m-2 sm:m-8">
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Sidebar: Search and Topics */}
        <div className="w-full lg:w-1/3 flex flex-col gap-6">
          {/* Search Input */}
          <div className="bg-gray-100 dark:bg-blue-900 rounded p-4">
            <div className="text-gray-800 mb-2 dark:text-white">{t('faq-search')}</div>
            <Input
              name="search"
              type="text"
              value={searchTerm}
              placeholder={t('faq-search-input')}
              color={theme === "dark" ? "white" : "gray"}
              onChange={handleSearch}
            />
          </div>

          {/* Topics List */}
          <div className="bg-gray-100 dark:bg-blue-900 rounded p-4">
            <div className="text-gray-800 mb-4 dark:text-white text-lg font-semibold">{t('faq-cat')}</div>
            <div className="flex flex-col gap-2">
              {faqData.map(({ label, topic }) => (
                <Button
                  key={topic}
                  variant={selectedTopic === topic && !searchTerm ? "filled" : "outlined"}
                  color={selectedTopic === topic && !searchTerm ? "blue" : "blue-gray"}
                  className={`rounded text-left justify-start px-4 overflow-ellipsis overflow-hidden whitespace-nowrap ${
                    selectedTopic === topic && !searchTerm
                      ? "bg-blue-700 text-white" 
                      : "dark:text-white dark:hover:bg-blue-1000 dark:bg-blue-900"
                  }`}
                  onClick={() => handleTopicSelect(topic)}
                >
                  {label[currentLang]}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content: FAQ Accordions */}
        <div className="w-full lg:w-2/3 flex flex-col gap-6 bg-white dark:bg-blue-950 rounded p-6">
          <div className="text-2xl font-bold text-blue-900 dark:text-yellow-800 mb-4">
            {searchTerm 
              ? t('faq-recommended')
              : faqData.find(item => item.topic === selectedTopic)?.label[currentLang]}
          </div>
          
          {faqsToDisplay.length > 0 ? (
            faqsToDisplay.map((faq, index) => (
              <div id={`faq${index}`} key={`faq${index}`}>
                <Accordion 
                  open={openTab === index} 
                  className="mb-2 rounded-lg border border-blue-gray-100 px-4 bg-gray-50 dark:bg-blue-900"
                >
                  <AccordionHeader
                    onClick={() => handleAccordionOpen(index, faq)}
                    className={`border-b-0 transition-colors text-blue-900 hover:text-blue-700 ${
                      openTab === index ? "text-blue-700 dark:text-white" : "dark:text-white"
                    }`}
                  >
                    <div className="flex flex-row gap-4">
                      <div>
                        {openTab === index ? 
                          <ChevronDownIcon className="h-5 w-5" /> : 
                          <ChevronRightIcon className="h-5 w-5" />
                        }
                      </div>
                      <div>{faq.question[currentLang]}</div>
                    </div>
                  </AccordionHeader>
                  <AccordionBody className="pt-0 text-base font-normal dark:text-white">
                    <div dangerouslySetInnerHTML={{ __html: faq.answer[currentLang] }}></div>
                  </AccordionBody>
                </Accordion>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-300">
              {t('faq-no-results')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Faq;